import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../../services/api';
import Timestamp from '../shared/Timestamp';
import './ContextAuthoring.css';
import OrgWideChatPanel from './OrgWideChatPanel';

// Barebone authoring page (edit org context -> save -> eval -> publish/rollback).
// Deliberately minimal UI — the whole page is slated for a design rework.

const IMPACT_POLL_MS = 5000;

// Field types the eval harness has a comparator for (TYPE_TO_RULE in eval_harness.py). Picking one
// is what stops a date or a number being scored as an exact string match.
const FIELD_TYPES = ['text', 'number', 'integer', 'float', 'date', 'datetime', 'time', 'boolean', 'list'];

const EMPTY_NEW_FIELD = { field_id: '', type: 'text', description: '' };

// Unique orgs across all scopes, for the Org select.
// The list now includes orgs that have never published: the API sends those as placeholder rows
// with has_context=false and no scope_key. Marking them here is what turns the page from
// "orgs that already have context" into "orgs you can give context to" — the onboarding case.
function buildOrgOptions(scopes) {
  const seen = new Map();
  scopes.forEach((s) => {
    const existing = seen.get(s.org_id);
    if (!existing) {
      seen.set(s.org_id, {
        org_id: s.org_id,
        label: s.org_display_name || s.orgname,
        hasContext: s.has_context !== false,
      });
    } else if (s.has_context !== false) {
      existing.hasContext = true;
    }
  });
  return Array.from(seen.values());
}

// The scope with no domain or pathway: the org's chat fragment on its own, which grounds every
// question no domain bundle serves. Offered for every org, so one whose base tiers seed no
// pathway -- or whose datasets carry no domain -- can still be given chat context.
const ORG_WIDE_KEY = '__org_wide__';
const ORG_WIDE_OPTION = { key: ORG_WIDE_KEY, label: 'Organization-wide (all questions)', domain: null, pathway: null };

// Distinct (domain, pathway) combos for one org, for the Pathway select.
// Domain is only shown in the label when the org spans more than one.
// Rows with has_context=false are orgs that have never published; they now carry the domain and
// pathway they COULD be authored into, so they belong here — without them a brand-new org had an
// empty pathway picker and nothing downstream to send. Rows with no domain get no pathway option
// of their own (no base tiers are seeded for them), but every org still gets the org-wide option.
function buildPathwayOptions(scopes, orgId) {
  const orgScopes = scopes.filter((s) => s.org_id === orgId && s.domain);
  const domains = new Set(orgScopes.map((s) => s.domain));
  const seen = new Set();
  return orgScopes.reduce((out, s) => {
    const key = `${s.domain}||${s.pathway || ''}`;
    if (seen.has(key)) return out;
    seen.add(key);
    const pathwayLabel = s.pathway || '(none)';
    out.push({
      key,
      label: domains.size > 1 ? `${s.domain} · ${pathwayLabel}` : pathwayLabel,
      domain: s.domain,
      pathway: s.pathway || null,
    });
    return out;
  }, [ORG_WIDE_OPTION]);
}

// Flatten effective {group: {field_id: attrs}} into editable rows, skipping
// the _chat pseudo-group (rendered separately as the chat fragment textarea)
// and any underscore-prefixed reserved field ids (e.g. _group_prompt).
function buildFieldRows(effective) {
  const rows = [];
  Object.entries(effective || {}).forEach(([groupName, fields]) => {
    if (groupName === '_chat') return;
    Object.entries(fields || {}).forEach(([fieldId, attrs]) => {
      if (fieldId.startsWith('_')) return;
      rows.push({
        group_name: groupName,
        field_id: fieldId,
        type: attrs?.type || 'text',
        description: attrs?.description || '',
        retrieval_text: attrs?.retrieval_text || '',
        // An org cannot delete an inherited field — the row belongs to the pathway. `hidden` is the
        // org-tier way to say "we do not collect this"; it drops the field from THIS org's bundle only.
        hidden: attrs?.hidden === true,
      });
    });
  });
  return rows;
}

// Org-tier chat fragment out of effective._chat._scope.layers, if present.
function extractChatFragment(effective) {
  const layers = effective?._chat?._scope?.layers;
  if (!Array.isArray(layers)) return '';
  const orgLayer = layers.find((l) => l.tier === 'org') || null;
  return orgLayer?.system_prompt || '';
}

function metricsHeadline(metrics) {
  if (!metrics || typeof metrics !== 'object') return null;
  const f1 = metrics.extraction?.pack_micro_f1 ?? metrics.pack_micro_f1;
  return typeof f1 === 'number' ? f1 : null;
}

// {field_id: {f1, ...}} nested under metrics.extraction, if present.
function perFieldF1(metrics) {
  const perField = metrics?.extraction?.per_field_f1 ?? metrics?.per_field_f1;
  return perField && typeof perField === 'object' ? Object.entries(perField) : [];
}

// A blocked-publish/gate reason is {metric, field, detail} — render its text, not the raw object.
function formatReason(reason) {
  if (!reason || typeof reason !== 'object') return String(reason);
  const parts = [reason.metric, reason.field].filter(Boolean).join(' / ');
  return parts ? `${parts}: ${reason.detail}` : reason.detail;
}

// PromoteResult carries "promoted"; RollbackResult doesn't — tell them apart to phrase the summary.
function formatPublishResult(result) {
  if (!result) return '';
  if ('promoted' in result) {
    if (!result.promoted) return `Blocked: ${result.reason ? formatReason(result.reason) : 'no reason given'}`;
    const base = `Published: ${result.from_version || '(none)'} -> ${result.to_version}`;
    // Production flipped but the authoring store didn't sync — the engineer must see it, not just the logs.
    return result.layers_staged === false
      ? `${base} — WARNING: authoring rows did not sync (layers_staged=false). Retry publish or contact eng.`
      : base;
  }
  return `Rolled back: ${result.from_version} -> ${result.to_version}`;
}

function formatChanged(changed) {
  if (!changed) return '';
  if (changed.baseline === false) return 'first run (no baseline)';
  const parts = [];
  const nFields = (changed.fields || []).length;
  const nPrompts = (changed.group_prompts || []).length;
  if (nFields) parts.push(`${nFields} field${nFields === 1 ? '' : 's'}`);
  if (nPrompts) parts.push(`${nPrompts} group prompt${nPrompts === 1 ? '' : 's'}`);
  if ((changed.groups || []).length) parts.push(`${changed.groups.length} group(s)`);
  if (changed.chat) parts.push('chat');
  if (changed.output_views) parts.push('output views');
  return parts.length ? parts.join(', ') : 'no change vs production';
}

function ContextAuthoring() {
  const [scopes, setScopes] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedPathwayKey, setSelectedPathwayKey] = useState('');
  const [selectedScopeKey, setSelectedScopeKey] = useState('');
  const [layers, setLayers] = useState(null);
  const [versions, setVersions] = useState(null);
  const [evalRuns, setEvalRuns] = useState([]);
  const [harvestable, setHarvestable] = useState([]); // "group|field|key" candidates
  const [fieldRows, setFieldRows] = useState([]);
  const [dirty, setDirty] = useState({}); // "group|field" -> {description?, retrieval_text?}
  const [chatFragment, setChatFragment] = useState('');
  const [chatDirty, setChatDirty] = useState(false);
  const [groupPrompts, setGroupPrompts] = useState({});
  const [groupPromptDirty, setGroupPromptDirty] = useState({});
  // Unsaved "add" forms: one per group, plus one for a brand-new group. An added row is an ordinary
  // dirty edit — it goes out on the same Save and rides the same candidate -> eval -> publish lane.
  const [newFieldDrafts, setNewFieldDrafts] = useState({});
  const [newGroupDraft, setNewGroupDraft] = useState({ group_name: '', ...EMPTY_NEW_FIELD });
  const [candidateVersion, setCandidateVersion] = useState(null);
  const [impact, setImpact] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [bundleId, setBundleId] = useState(() => localStorage.getItem('context_authoring_bundle_id') || '');
  const [datasetId, setDatasetId] = useState(() => localStorage.getItem('context_authoring_dataset_id') || '');
  const [evalPacks, setEvalPacks] = useState([]);
  // "Create eval pack": upload inputs + ground truth, then the one-time ingest that makes the
  // pack usable. Previously both steps needed a shell, which blocked onboarding a new org.
  const [showNewPack, setShowNewPack] = useState(false);
  const [newPackId, setNewPackId] = useState('');
  const [newPackGt, setNewPackGt] = useState(null);
  const [newPackInputs, setNewPackInputs] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  // The org-wide panel keeps its own busy state; the Org and Pathway selects also wait on it, so a
  // publish can't be orphaned by switching scope mid-request.
  const [panelBusy, setPanelBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollTimerRef = useRef(null);

  const selectedScope = scopes.find((s) => s.scope_key && s.scope_key === selectedScopeKey) || null;
  const orgOptions = buildOrgOptions(scopes);
  const pathwayOptions = selectedOrgId ? buildPathwayOptions(scopes, selectedOrgId) : [];

  useEffect(() => {
    apiService.getContextScopes()
      .then((data) => setScopes(data.scopes || []))
      .catch((e) => setError(e.message));
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const loadEvalPacks = useCallback(
    () => apiService.getEvalPacks()
      .then((data) => setEvalPacks(data.eval_packs || []))
      .catch((e) => setError(e.message)),
    [],
  );

  useEffect(() => {
    loadEvalPacks();
  }, [loadEvalPacks]);

  const loadScope = useCallback(async (scope) => {
    setBusy(true);
    setError('');
    try {
      const [layersData, versionsData, runsData, harvestData] = await Promise.all([
        apiService.getContextLayers(scope.domain, scope.org_id, scope.pathway),
        apiService.getContextVersions(scope.scope_key),
        // Run history is additive context, so a failure here must not blank the whole panel.
        apiService.getContextEvalRuns(scope.scope_key).catch(() => ({ runs: [] })),
        apiService.getHarvestCandidates(scope.domain, scope.org_id, scope.pathway).catch(() => ({ candidates: [] })),
      ]);
      setLayers(layersData);
      setVersions(versionsData);
      setEvalRuns(runsData.runs || []);
      setHarvestable(harvestData.candidates || []);
      // Re-derive the publishable candidate: the newest scored version created after Production.
      // Without this, a page refresh strands an already-scored candidate — Publish stays disabled
      // until another eval is run, costing minutes to re-earn a result we already have. Older
      // scored versions are excluded so this can only ever offer a forward promotion; going
      // backwards to a superseded version is what Rollback is for.
      const productionVersion = versionsData?.pointer?.version;
      const allVersions = versionsData?.versions || [];
      const productionCreatedAt = allVersions.find((v) => v.version === productionVersion)?.created_at;
      const publishable = allVersions
        .filter((v) => {
          if (!v.has_metrics || v.version === productionVersion) return false;
          if (!productionVersion) return true; // never published — any scored version is a first publish
          // Pointer exists but that version isn't in history. Admitting every scored row here
          // would light up Publish on a version older than production (Rollback's job).
          if (!productionCreatedAt) return false;
          return new Date(v.created_at) > new Date(productionCreatedAt);
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      setCandidateVersion(publishable ? publishable.version : null);
      setFieldRows(buildFieldRows(layersData.effective));
      setChatFragment(extractChatFragment(layersData.effective));
      setGroupPrompts(layersData.group_prompts || {});
      setGroupPromptDirty({});
      setDirty({});
      setChatDirty(false);
      setNewFieldDrafts({});
      setNewGroupDraft({ group_name: '', ...EMPTY_NEW_FIELD });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const resetScopeState = () => {
    setLayers(null);
    setVersions(null);
    setEvalRuns([]);
    setHarvestable([]);
    setFieldRows([]);
    setDirty({});
    setChatFragment('');
    setChatDirty(false);
    setGroupPrompts({});
    setGroupPromptDirty({});
    setNewFieldDrafts({});
    setNewGroupDraft({ group_name: '', ...EMPTY_NEW_FIELD });
    setCandidateVersion(null);
    setImpact(null);
    setPublishResult(null);
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setPolling(false);
  };

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setSelectedPathwayKey('');
    setSelectedScopeKey('');
    resetScopeState();
  };

  const handlePathwayChange = (e) => {
    const key = e.target.value;
    setSelectedPathwayKey(key);
    resetScopeState();
    if (!key || key === ORG_WIDE_KEY) {
      // The org-wide scope has no scope_key: OrgWideChatPanel loads and publishes it by org alone.
      setSelectedScopeKey('');
      return;
    }
    const [domain, rawPathway] = key.split('||');
    const pathway = rawPathway || null;
    const scope = scopes.find(
      (s) => s.org_id === selectedOrgId && s.domain === domain && (s.pathway || null) === pathway
    );
    setSelectedScopeKey(scope ? scope.scope_key : '');
    if (scope) loadScope(scope);
  };

  const handleEvalPackChange = (e) => {
    const packBundleId = e.target.value;
    const pack = evalPacks.find((p) => p.bundle_id === packBundleId);
    setBundleId(packBundleId);
    setDatasetId(pack ? pack.dataset_id : '');
  };

  // Promoting a value up a tier changes the DEFAULT every other org in the scope inherits, so the
  // confirm says so in those words. The server still enforces the real guarantee: the source org's
  // compiled context must be unchanged, or the whole harvest rolls back.
  const handleHarvest = async (row, key, targetTier) => {
    const c = harvestable.find(
      (h) => h.group_name === row.group_name && h.field_id === row.field_id && h.key === key,
    );
    if (!c) return;
    const msg =
      `Move ${key} on ${row.group_name}/${row.field_id} up to the ${targetTier} tier?\n\n` +
      `This org keeps the same compiled context — that is checked and rolled back if it changes.\n\n` +
      `WARNING: every OTHER org in ${selectedScope.domain} that has not overridden ${key} will ` +
      `inherit this text at their next publish. Their extraction quality is NOT re-measured by this ` +
      `action, so a value that helps this org may HURT theirs. Re-run their evals after promoting.\n\n` +
      `Promoting: ${c.org_value_preview}\n` +
      `Replaces inherited: ${c.inherited_value_preview || '(nothing)'}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiService.harvestContextLayers(
        selectedScope.org_id,
        selectedScope.domain,
        selectedScope.pathway,
        targetTier,
        [{ group_name: row.group_name, field_id: row.field_id, keys: [key] }],
      );
      setStatus(`Promoted ${key} to ${targetTier} (invariant ${res.invariant}).`);
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleFieldEdit = (row, attr, value) => {
    const key = `${row.group_name}|${row.field_id}`;
    setFieldRows((rows) => rows.map((r) =>
      r.group_name === row.group_name && r.field_id === row.field_id ? { ...r, [attr]: value } : r
    ));
    setDirty((d) => ({ ...d, [key]: { ...(d[key] || {}), [attr]: value } }));
  };

  // Add a field to a group (or, with groupName supplied by the new-group form, create the group by
  // giving it its first field). The row is appended locally and marked dirty — nothing is written
  // until Save, so an add is indistinguishable from an edit for the rest of the lifecycle.
  const addFieldRow = (groupName, draft) => {
    const group = groupName.trim();
    const fieldId = (draft.field_id || '').trim();
    if (!group) return 'Group name is required.';
    if (!fieldId) return 'Field id is required.';
    if (fieldRows.some((r) => r.group_name === group && r.field_id === fieldId)) {
      return `${group} already has a field called "${fieldId}".`;
    }
    const row = {
      group_name: group,
      field_id: fieldId,
      type: draft.type || 'text',
      description: draft.description || '',
      retrieval_text: '',
      hidden: false,
      isNew: true,
    };
    setFieldRows((rows) => [...rows, row]);
    setDirty((d) => ({
      ...d,
      [`${group}|${fieldId}`]: { type: row.type, description: row.description },
    }));
    return null;
  };

  const handleAddField = (groupName) => {
    const draft = newFieldDrafts[groupName] || EMPTY_NEW_FIELD;
    const problem = addFieldRow(groupName, draft);
    if (problem) { setError(problem); return; }
    setError('');
    setNewFieldDrafts((d) => ({ ...d, [groupName]: { ...EMPTY_NEW_FIELD } }));
  };

  const handleAddGroup = () => {
    const group = (newGroupDraft.group_name || '').trim();
    if (fieldRows.some((r) => r.group_name === group)) {
      setError(`Group "${group}" already exists.`);
      return;
    }
    const problem = addFieldRow(group, newGroupDraft);
    if (problem) { setError(problem); return; }
    setError('');
    setNewGroupDraft({ group_name: '', ...EMPTY_NEW_FIELD });
  };

  const handleGroupPromptEdit = (groupName, value) => {
    setGroupPrompts((gp) => ({ ...gp, [groupName]: value }));
    setGroupPromptDirty((d) => ({ ...d, [groupName]: true }));
  };

  // Hiding a whole group is just hiding each of its fields — no group-level row, no second
  // mechanism. Compile drops a group once nothing visible is left in it.
  const handleSetGroupHidden = (groupName, hidden) => {
    const affected = fieldRows.filter((r) => r.group_name === groupName && r.hidden !== hidden);
    if (affected.length === 0) return;
    setFieldRows((rows) => rows.map((r) => (r.group_name === groupName ? { ...r, hidden } : r)));
    setDirty((d) => {
      const next = { ...d };
      affected.forEach((r) => {
        const key = `${r.group_name}|${r.field_id}`;
        next[key] = { ...(next[key] || {}), hidden };
      });
      return next;
    });
  };

  const hasPendingCandidate = (row) => {
    const orgRows = layers?.org_rows || [];
    return orgRows.some((r) =>
      r.group_name === row.group_name && r.field_id === row.field_id && r.status === 'candidate'
    );
  };

  const handleSave = async () => {
    if (!selectedScope) return;
    setBusy(true);
    setError('');
    setStatus('');
    try {
      const fields = Object.entries(dirty).map(([key, attributes]) => {
        const [groupName, fieldId] = key.split('|');
        return { group_name: groupName, field_id: fieldId, attributes };
      });
      // A blank prompt is rejected server-side (cannot clear an existing group prompt). Drop it
      // here so an untouched textarea on a just-created group cannot turn the whole save into a
      // 422; the previous text is kept on refetch.
      const group_prompts = Object.fromEntries(
        Object.keys(groupPromptDirty)
          .filter((g) => groupPromptDirty[g] && (groupPrompts[g] || '').trim())
          .map((g) => [g, groupPrompts[g]])
      );
      const result = await apiService.saveContextLayers(
        selectedScope.org_id,
        fields,
        chatDirty ? chatFragment : null,
        group_prompts
      );
      setStatus(`Saved (${result.rows_written} rows written)`);
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pollImpact = useCallback(async (version, scopeKey) => {
    try {
      const result = await apiService.getContextCandidateImpact(version, scopeKey);
      setImpact(result);
      if (result.status === 'running') {
        pollTimerRef.current = setTimeout(() => pollImpact(version, scopeKey), IMPACT_POLL_MS);
      } else {
        setPolling(false);
      }
    } catch (e) {
      setError(e.message);
      setPolling(false);
    }
  }, []);

  const handleCreatePack = async () => {
    if (!newPackId.trim() || !newPackGt || newPackInputs.length === 0) return;
    setBusy(true);
    setError('');
    const packId = newPackId.trim();
    try {
      // Two phases, reported separately: the upload returns in seconds, the ingest drives the
      // real OCR pipeline and can run for minutes. Upload success is durable even if ingest's
      // hop times out (App Gateway / nginx often die first) — stamp the pack id so a later
      // refresh can pick up a still-running ingest instead of stranding it.
      setStatus(`Uploading ${newPackInputs.length} file(s) to ${packId}…`);
      await apiService.createEvalPack(packId, newPackGt, newPackInputs);
      setBundleId(packId);
      localStorage.setItem('context_authoring_bundle_id', packId);
      setStatus(`Uploaded. Ingesting ${packId} — this can take several minutes…`);
      try {
        const ingested = await apiService.ingestEvalPack(packId, selectedScope?.pathway || null);
        await loadEvalPacks();
        setDatasetId(ingested.dataset_id || '');
        if (ingested.dataset_id) localStorage.setItem('context_authoring_dataset_id', ingested.dataset_id);
        setStatus(`Eval pack ${packId} is ready (dataset ${ingested.dataset_id}).`);
        setShowNewPack(false);
        setNewPackId('');
        setNewPackGt(null);
        setNewPackInputs([]);
      } catch (e) {
        await loadEvalPacks();
        setError(
          `${e.message}. Ingest may still be running — refresh eval packs before retrying.`
        );
        setStatus('');
      }
    } catch (e) {
      setError(e.message);
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const handleRunEval = async () => {
    if (!selectedScope || !bundleId || !datasetId) return;
    setBusy(true);
    setError('');
    setStatus('');
    localStorage.setItem('context_authoring_bundle_id', bundleId);
    localStorage.setItem('context_authoring_dataset_id', datasetId);
    try {
      // Compile mints a fresh candidate from the current saved draft; only then do we evaluate it.
      const compiled = await apiService.compileContextCandidate(
        selectedScope.domain,
        selectedScope.pathway,
        selectedScope.org_id
      );
      const version = compiled.version;
      setCandidateVersion(version);
      setImpact(null);
      setPublishResult(null);

      await apiService.evaluateContextCandidate(selectedScope.scope_key, version, bundleId, datasetId);
      setStatus(`Eval started for ${version}`);
      setImpact({ status: 'running' });
      setPolling(true);
      pollTimerRef.current = setTimeout(
        () => pollImpact(version, selectedScope.scope_key),
        IMPACT_POLL_MS
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedScope || !candidateVersion) return;
    setBusy(true);
    setError('');
    setStatus('');
    try {
      const result = await apiService.publishContextCandidate(
        selectedScope.scope_key,
        candidateVersion
      );
      setPublishResult(result);
      setStatus(result.promoted
        ? `Published: ${result.from_version || '(none)'} -> ${result.to_version}`
        : `Publish blocked: ${result.reason ? formatReason(result.reason) : 'see result below'}`);
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePublishChatOnly = async () => {
    if (!selectedScope) return;
    setBusy(true);
    setError('');
    setStatus('');
    try {
      // Persist the chat-fragment edit, then the server grafts the merged chat prompt onto the
      // LIVE production bundle and promotes it (gate_lane "chat_only_skip"). Field/group-prompt
      // drafts stay pending — they can neither block this publish nor ride along into production.
      await apiService.saveContextLayers(selectedScope.org_id, [], chatDirty ? chatFragment : null, null);
      setChatDirty(false);
      const result = await apiService.publishContextChatUpdate(
        selectedScope.domain,
        null,
        selectedScope.org_id
      );
      setPublishResult(result);
      setStatus(
        result.promoted
          ? `Published (chat-only, no eval): ${result.from_version || '(none)'} -> ${result.to_version}`
          : `Publish blocked: ${result.reason ? formatReason(result.reason) : 'see result below'}`
      );
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async (row) => {
    // row = one slot; null = discard every draft for the org. Reverts to what production serves.
    if (!selectedScope) return;
    setBusy(true);
    setError('');
    setStatus('');
    try {
      const fields = row ? [{ group_name: row.group_name, field_id: row.field_id }] : null;
      const result = await apiService.discardContextLayers(selectedScope.org_id, fields);
      setStatus(`Discarded ${result.rows_discarded} draft edit(s)`);
      if (row) setDirty((d) => { const n = { ...d }; delete n[`${row.group_name}|${row.field_id}`]; return n; });
      else setDirty({});
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRollback = async () => {
    if (!selectedScope) return;
    setBusy(true);
    setError('');
    setStatus('');
    setPublishResult(null);
    try {
      const result = await apiService.rollbackContextScope(selectedScope.scope_key);
      setPublishResult(result);
      setStatus(`Rolled back to ${result.to_version}`);
      await loadScope(selectedScope);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pendingPromptCount = Object.keys(groupPromptDirty).filter(
    (g) => groupPromptDirty[g] && (groupPrompts[g] || '').trim()
  ).length;
  const droppedBlankPrompt = Object.keys(groupPromptDirty).some(
    (g) => groupPromptDirty[g] && !(groupPrompts[g] || '').trim()
  );
  const dirtyCount = Object.keys(dirty).length + (chatDirty ? 1 : 0) + pendingPromptCount;
  const candidateF1 = metricsHeadline(impact?.candidate_metrics);
  const productionF1 = metricsHeadline(impact?.production_metrics);
  const deltaF1 = typeof impact?.delta === 'number' ? impact.delta : null;
  const perFieldRows = perFieldF1(impact?.candidate_metrics);

  return (
    <div className="context-authoring">
      <h2>Context Authoring</h2>

      {error && <div className="ca-error">{error}</div>}

      <div className="ca-section">
        <label>
          Org:{' '}
          <select className="resui-select" value={selectedOrgId} onChange={handleOrgChange} disabled={busy || panelBusy}>
            <option value="">-- select an org --</option>
            {orgOptions.map((o) => (
              <option key={o.org_id} value={o.org_id}>
                {o.hasContext ? o.label : `${o.label} (new — no context yet)`}
              </option>
            ))}
          </select>
        </label>{' '}
        <label>
          Pathway:{' '}
          <select className="resui-select" value={selectedPathwayKey} onChange={handlePathwayChange} disabled={busy || panelBusy || !selectedOrgId}>
            <option value="">-- select a pathway --</option>
            {pathwayOptions.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </label>
        {selectedScope && (
          <span className="ca-scope-meta">
            {' '}org: {selectedScope.org_display_name || selectedScope.orgname}
          </span>
        )}
      </div>

      {layers && (
        <>
          <div className="ca-section ca-actions">
            <button onClick={handleSave} disabled={busy || dirtyCount === 0}>
              Save{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
            </button>
            {droppedBlankPrompt && (
              <span className="ca-note">
                Blank group prompts are not saved — the previous text is kept.
              </span>
            )}
            {candidateVersion && <span className="ca-candidate">candidate: {candidateVersion}</span>}
            <label>
              Eval pack:{' '}
              <select className="resui-select resui-select--compact" value={bundleId} onChange={handleEvalPackChange} disabled={busy}>
                <option value="">-- select an eval pack --</option>
                {evalPacks.map((p) => (
                  <option key={p.bundle_id} value={p.bundle_id} disabled={p.ready === false}>
                    {(p.dataset_name || p.bundle_id) + (p.ready === false ? ' (not ingested)' : '')}
                  </option>
                ))}
              </select>
            </label>
            {evalPacks.length === 0 && <span className="ca-note">no eval packs available</span>}
            <button onClick={() => setShowNewPack((v) => !v)} disabled={busy}>
              {showNewPack ? 'Cancel new pack' : 'Create eval pack'}
            </button>
            <button onClick={handleRunEval} disabled={busy || polling || !bundleId || !datasetId}>
              Run eval
            </button>
            <button onClick={handlePublish} disabled={busy || !candidateVersion}>Publish</button>
            <button onClick={handlePublishChatOnly} disabled={busy || polling || !chatDirty}>
              Publish chat update (no eval)
            </button>
            <button
              onClick={() => handleDiscard(null)}
              disabled={busy || !(layers?.org_rows || []).some((r) => r.status === 'candidate')}
              title="Withdraw every draft edit for this org — fields revert to what production serves"
            >
              Discard all drafts
            </button>
            <button onClick={handleRollback} disabled={busy}>Rollback</button>
            {status && <div className="ca-status">{status}</div>}
          </div>

          {showNewPack && (
            <div className="ca-section">
              <h3>New eval pack</h3>
              <p className="ca-note">
                The ground-truth CSV needs a <code>file_name</code> column naming each input file,
                and every input file must have exactly one matching row. Ingest runs once per pack —
                later evals replay these documents rather than re-ingesting them. Gateway and WAF
                cap the upload size; a 413 means the pack is too large — split the documents rather
                than retrying.
              </p>
              <label>
                Pack name:{' '}
                <input
                  type="text"
                  className="resui-input"
                  value={newPackId}
                  onChange={(e) => setNewPackId(e.target.value)}
                  placeholder="acme-stroke-gt"
                  disabled={busy}
                />
              </label>
              <label>
                Ground truth (.csv):{' '}
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setNewPackGt(e.target.files?.[0] || null)}
                  disabled={busy}
                />
              </label>
              <label>
                Input documents:{' '}
                <input
                  type="file"
                  multiple
                  onChange={(e) => setNewPackInputs(Array.from(e.target.files || []))}
                  disabled={busy}
                />
              </label>
              {newPackInputs.length > 0 && (
                <span className="ca-note">{newPackInputs.length} file(s) selected</span>
              )}
              <button
                onClick={handleCreatePack}
                disabled={busy || !newPackId.trim() || !newPackGt || newPackInputs.length === 0}
              >
                Upload and ingest
              </button>
            </div>
          )}

          {impact && (
            <div className="ca-section">
              <h3>Impact</h3>
              {impact.status === 'running' && <p>Evaluating…</p>}
              {impact.status === 'not_evaluated' && <p>Not evaluated yet.</p>}
              {impact.status === 'failed' && (
                <div className="ca-error">Eval failed: {impact.error || 'unknown error'}</div>
              )}
              {impact.status === 'done' && (
                <>
                  <table className="ca-table ca-impact-table">
                    <tbody>
                      <tr><th>Candidate F1</th><td>{candidateF1 ?? 'n/a'}</td></tr>
                      <tr><th>Production F1</th><td>{productionF1 ?? 'n/a'}</td></tr>
                      <tr>
                        <th>Delta</th>
                        <td>{deltaF1 != null ? `${deltaF1 >= 0 ? '+' : ''}${deltaF1}` : 'n/a'}</td>
                      </tr>
                      <tr>
                        <th>Gate</th>
                        <td>{impact.gate_preview?.would_pass ? 'PASS' : 'BLOCKED'}</td>
                      </tr>
                      {!impact.gate_preview?.would_pass && impact.gate_preview?.reason && (
                        <tr><th>Reason</th><td>{formatReason(impact.gate_preview.reason)}</td></tr>
                      )}
                    </tbody>
                  </table>
                  {perFieldRows.length > 0 && (
                    <table className="ca-table ca-per-field-table">
                      <thead><tr><th>Field</th><th>F1</th></tr></thead>
                      <tbody>
                        {perFieldRows.map(([field, v]) => (
                          <tr key={field}>
                            <td>{field}</td>
                            <td>{typeof v?.f1 === 'number' ? v.f1 : (typeof v === 'number' ? v : 'n/a')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}

          {publishResult && (
            <div className="ca-section">
              <h3>Publish / rollback result</h3>
              <p>{formatPublishResult(publishResult)}</p>
            </div>
          )}

          <div className="ca-section">
            <h3>Eval runs</h3>
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Candidate</th>
                  <th>F1</th>
                  <th>Changed</th>
                  <th>Status</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {evalRuns.map((r) => (
                  <tr key={r.id}>
                    <td><Timestamp value={r.started_at} /></td>
                    <td>{r.candidate_version}</td>
                    <td>{r.headline_f1 === null || r.headline_f1 === undefined ? '' : r.headline_f1.toFixed(4)}</td>
                    <td>{formatChanged(r.changed)}</td>
                    <td>{r.status === 'failed' ? <span title={r.error || ''}>failed</span> : r.status}</td>
                    <td>{r.actor}</td>
                  </tr>
                ))}
                {evalRuns.length === 0 && (
                  <tr><td colSpan={6}>No eval runs yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ca-section">
            <h3>Version history</h3>
            <table className="ca-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Scored</th>
                </tr>
              </thead>
              <tbody>
                {(versions?.versions || []).map((v) => {
                  const isProduction = versions?.pointer?.version === v.version;
                  const isPrevious = versions?.pointer?.previous_version === v.version;
                  return (
                    <tr key={v.version} className={isProduction ? 'ca-row-production' : ''}>
                      <td>{v.version}</td>
                      <td>{isProduction ? <strong>PRODUCTION</strong> : (isPrevious ? 'previous' : '')}</td>
                      <td><Timestamp value={v.created_at} /></td>
                      <td>{v.has_metrics ? 'yes' : 'no'}</td>
                    </tr>
                  );
                })}
                {(versions?.versions || []).length === 0 && (
                  <tr><td colSpan={4}>No versions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ca-section">
            <h3>Fields</h3>
            {harvestable.length > 0 && (
              <p className="ca-warning">
                {'\u26A0'} Promoting an attribute ({'\u2191'} buttons) makes it the default for every org in{' '}
                <strong>{selectedScope.domain}</strong> that has not overridden it. Their extraction quality is
                not re-measured here — re-run their evals afterwards.
              </p>
            )}
            {fieldRows.length === 0 && <p>No fields in effective context.</p>}
            {[...new Set(fieldRows.map((r) => r.group_name))].sort().map((groupName) => (
              <div key={groupName}>
                <h4>
                  {groupName}{' '}
                  <button
                    onClick={() => handleSetGroupHidden(groupName, true)}
                    disabled={busy || fieldRows.filter((r) => r.group_name === groupName).every((r) => r.hidden)}
                    title="Mark every field in this group as not collected here — the group leaves this org's bundle"
                  >
                    hide group
                  </button>{' '}
                  <button
                    onClick={() => handleSetGroupHidden(groupName, false)}
                    disabled={busy || fieldRows.filter((r) => r.group_name === groupName).every((r) => !r.hidden)}
                  >
                    show group
                  </button>
                </h4>
                <textarea
                  className="ca-chat-fragment"
                  rows={3}
                  value={groupPrompts[groupName] ?? ''}
                  onChange={(e) => handleGroupPromptEdit(groupName, e.target.value)}
                  disabled={busy}
                  title="Clearing a group prompt is not supported; a blank save keeps the previous text."
                />
                <table className="ca-table">
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Field</th>
                      <th>Type</th>
                      <th title="Not collected at this org — the field leaves this org's bundle only">
                        Hidden
                      </th>
                      <th>Description</th>
                      <th>Retrieval text</th>
                      <th>Pending</th>
                      <th>Promote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fieldRows.filter((row) => row.group_name === groupName).map((row) => (
                      <tr key={`${row.group_name}|${row.field_id}`} className={row.hidden ? 'ca-row-hidden' : ''}>
                        <td>{row.group_name}</td>
                        <td>{row.field_id}{row.isNew ? ' (new)' : ''}</td>
                        <td>{row.type}</td>
                        <td>
                          <label className="resui-checkbox">
                            <input
                              type="checkbox"
                              className="resui-checkbox-input"
                              checked={row.hidden}
                              onChange={(e) => handleFieldEdit(row, 'hidden', e.target.checked)}
                              disabled={busy}
                              title="We do not collect this field. Reversible — untick and save to bring it back."
                            />
                            <span className="resui-checkbox-mark" />
                          </label>
                        </td>
                        <td>
                          <textarea
                            rows={2}
                            value={row.description}
                            onChange={(e) => handleFieldEdit(row, 'description', e.target.value)}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="resui-input resui-input--compact"
                            value={row.retrieval_text}
                            onChange={(e) => handleFieldEdit(row, 'retrieval_text', e.target.value)}
                            disabled={busy}
                          />
                        </td>
                        <td>
                          {hasPendingCandidate(row) ? (
                            <>
                              <span className="ca-pending">pending</span>{' '}
                              <button
                                className="ca-discard"
                                title="Withdraw this draft edit — the field reverts to what production serves"
                                onClick={() => handleDiscard(row)}
                                disabled={busy}
                              >
                                discard
                              </button>
                            </>
                          ) : ''}
                        </td>
                        <td>
                          {harvestable
                            .filter((h) => h.group_name === row.group_name && h.field_id === row.field_id)
                            .map((h) => (
                              <button
                                key={h.key}
                                type="button"
                                className="ca-harvest-btn"
                                title={`Move ${h.key} up to the ${h.target_suggestion} tier so other orgs inherit it`}
                                onClick={() => handleHarvest(row, h.key, h.target_suggestion)}
                                disabled={busy || hasPendingCandidate(row)}
                              >
                                {`\u2191 ${h.key}`}
                              </button>
                            ))}
                        </td>
                      </tr>
                    ))}
                    <tr className="ca-add-row">
                      <td>{groupName}</td>
                      <td>
                        <input
                          type="text"
                          className="resui-input resui-input--compact"
                          placeholder="new field id"
                          value={newFieldDrafts[groupName]?.field_id ?? ''}
                          onChange={(e) => setNewFieldDrafts((d) => ({
                            ...d,
                            [groupName]: { ...EMPTY_NEW_FIELD, ...(d[groupName] || {}), field_id: e.target.value },
                          }))}
                          disabled={busy}
                        />
                      </td>
                      <td>
                        <select
                          className="resui-select resui-select--compact"
                          value={newFieldDrafts[groupName]?.type ?? 'text'}
                          onChange={(e) => setNewFieldDrafts((d) => ({
                            ...d,
                            [groupName]: { ...EMPTY_NEW_FIELD, ...(d[groupName] || {}), type: e.target.value },
                          }))}
                          disabled={busy}
                        >
                          {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td />
                      <td>
                        <textarea
                          rows={2}
                          placeholder="what this field means"
                          value={newFieldDrafts[groupName]?.description ?? ''}
                          onChange={(e) => setNewFieldDrafts((d) => ({
                            ...d,
                            [groupName]: { ...EMPTY_NEW_FIELD, ...(d[groupName] || {}), description: e.target.value },
                          }))}
                          disabled={busy}
                        />
                      </td>
                      <td />
                      <td>
                        <button
                          onClick={() => handleAddField(groupName)}
                          disabled={busy || !(newFieldDrafts[groupName]?.field_id || '').trim()}
                          title="Add this field to the group — saved as a draft, published like any other edit"
                        >
                          add field
                        </button>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}

            <div className="ca-add-group">
              <h4>Add a group</h4>
              <p className="ca-note">
                A group needs its first field. Both are drafts until you Save, and they publish through
                the same eval and gate as any other change.
              </p>
              <input
                type="text"
                className="resui-input"
                placeholder="new group name"
                value={newGroupDraft.group_name}
                onChange={(e) => setNewGroupDraft((d) => ({ ...d, group_name: e.target.value }))}
                disabled={busy}
              />{' '}
              <input
                type="text"
                className="resui-input"
                placeholder="first field id"
                value={newGroupDraft.field_id}
                onChange={(e) => setNewGroupDraft((d) => ({ ...d, field_id: e.target.value }))}
                disabled={busy}
              />{' '}
              <select
                className="resui-select"
                value={newGroupDraft.type}
                onChange={(e) => setNewGroupDraft((d) => ({ ...d, type: e.target.value }))}
                disabled={busy}
              >
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>{' '}
              <input
                type="text"
                className="resui-input"
                placeholder="field description"
                value={newGroupDraft.description}
                onChange={(e) => setNewGroupDraft((d) => ({ ...d, description: e.target.value }))}
                disabled={busy}
              />{' '}
              <button
                onClick={handleAddGroup}
                disabled={busy || !newGroupDraft.group_name.trim() || !newGroupDraft.field_id.trim()}
              >
                add group
              </button>
            </div>
          </div>

          <div className="ca-section">
            <h3>Chat fragment (one per org, all pathways)</h3>
            <textarea
              className="ca-chat-fragment"
              rows={4}
              value={chatFragment}
              onChange={(e) => { setChatFragment(e.target.value); setChatDirty(true); }}
              disabled={busy}
            />
          </div>
        </>
      )}

      {selectedOrgId && selectedPathwayKey === ORG_WIDE_KEY && <OrgWideChatPanel orgId={selectedOrgId} onBusyChange={setPanelBusy} />}

      {busy && !layers && selectedScopeKey && <p>Loading scope...</p>}
    </div>
  );
}

export default ContextAuthoring;
